import { useEffect, useRef } from 'react';
import { BookOpen, KeyRound, Settings, User, Users } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ProfileTab =
  | 'personal'
  | 'parent'
  | 'academic'
  | 'preferences'
  | 'password'
  | 'settings';

interface ProfileTabBarProps {
  activeTab: ProfileTab;
  onSelectTab: (tab: ProfileTab) => void;
  showPasswordTab?: boolean;
  showSettingsTab?: boolean;
}

const TABS: { key: ProfileTab; label: string; icon: typeof User }[] = [
  { key: 'personal', label: 'Personal Information', icon: User },
  { key: 'parent', label: 'Parent / Guardian', icon: Users },
  { key: 'academic', label: 'Academic Details', icon: BookOpen },
  { key: 'password', label: 'Change Password', icon: KeyRound },
  { key: 'settings', label: 'Settings', icon: Settings },
];

export function ProfileTabBar({
  activeTab,
  onSelectTab,
  showPasswordTab = true,
  showSettingsTab = true,
}: ProfileTabBarProps) {
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    const el = tabRefs.current[activeTab];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [activeTab]);

  const visibleTabs = TABS.filter((tab) => {
    if (tab.key === 'password' && !showPasswordTab) return false;
    if (tab.key === 'settings' && !showSettingsTab) return false;
    return true;
  });

  return (
    <div className="flex items-center gap-6 sm:gap-8 border-b border-slate-200 dark:border-white/10 overflow-x-auto pb-0.5 [::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
      {visibleTabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.key;
        return (
          <button
            key={tab.key}
            ref={(el) => {
              tabRefs.current[tab.key] = el;
            }}
            type="button"
            onClick={(e) => {
              onSelectTab(tab.key);
              e.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
            }}
            className={cn(
              'flex items-center gap-2 pb-3 text-xs sm:text-[14px] font-bold transition-all cursor-pointer whitespace-nowrap relative shrink-0',
              isActive
                ? 'text-slate-700 dark:text-white font-extrabold'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200',
            )}
          >
            <Icon className={cn('h-4 w-4', isActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400')} />
            <span>{tab.label}</span>
            {isActive && (
              <span className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full" />
            )}
          </button>
        );
      })}
    </div>
  );
}
